import { useMemo } from 'react';
import { styled } from '@mui/material/styles';
import VerifiedIcon from '@mui/icons-material/Verified';
import IosShareIcon from '@mui/icons-material/IosShare';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTranslation } from '@hooks';
import { MEDIA_TYPE } from '@constants/file';
import { PhoneFrame, BrandAvatar } from './styles';
import { GOOGLE_CTA } from './utils';
import MediaSlide from './MediaSlide';

const ROBOTO = 'Roboto, "Google Sans", Arial, sans-serif';

const Card = styled(PhoneFrame)({ fontFamily: ROBOTO, color: '#202124' });

const HeaderRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '14px 12px 12px 16px',
});

const AvatarWrap = styled('div')({ position: 'relative', flexShrink: 0 });

const VerifiedBadge = styled(VerifiedIcon)({
    position: 'absolute',
    right: -4,
    bottom: -4,
    fontSize: 18,
    color: '#1a73e8',
    backgroundColor: '#fff',
    borderRadius: '50%',
});

const TitleColumn = styled('div')({ flex: 1, minWidth: 0 });

const BusinessName = styled('div')({
    fontWeight: 500,
    fontSize: 16,
    color: '#202124',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
});

const DateLine = styled('div')({
    color: '#5f6368',
    fontSize: 14,
    marginTop: 2,
});

const HeaderActions = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    color: '#3c4043',
    '& svg': { fontSize: 22 },
});

const Body = styled('div')({
    padding: '12px 16px 16px',
    fontSize: 14,
    lineHeight: '20px',
    color: '#3c4043',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
});

const CtaWrap = styled('div')({ padding: '0 16px 16px' });

const CtaPill = styled('div')({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    backgroundColor: '#cfecea',
    color: '#0c4845',
    borderRadius: 999,
    padding: '10px 18px',
    fontSize: 15,
    fontWeight: 500,
    '& svg': { fontSize: 20 },
});

const PlatformCardGoogle = ({ pageName, locationLogo, message, media, dateLabel, button }) => {
    const { t } = useTranslation();

    // Google publishes at most one image and no video.
    const singleImage = useMemo(() => {
        const first = (media || []).find(m => m?.link && m?.type === MEDIA_TYPE.IMAGE);
        return first ? [first] : [];
    }, [media]);

    const cta = button ? GOOGLE_CTA[button] : null;

    return (
        <Card>
            <HeaderRow>
                <AvatarWrap>
                    <BrandAvatar name={pageName} image={locationLogo} size={40} color="#1a73e8" />
                    <VerifiedBadge />
                </AvatarWrap>
                <TitleColumn>
                    <BusinessName>{pageName}</BusinessName>
                    {dateLabel && <DateLine>{dateLabel}</DateLine>}
                </TitleColumn>
                <HeaderActions>
                    <IosShareIcon />
                    <MoreVertIcon />
                </HeaderActions>
            </HeaderRow>

            {singleImage.length > 0 && <MediaSlide media={singleImage} aspectRatio="4 / 3" allowVideo={false} />}

            {message && <Body>{message}</Body>}

            {cta && (
                <CtaWrap>
                    <CtaPill>
                        <cta.Icon />
                        <span>{t(cta.labelKey)}</span>
                    </CtaPill>
                </CtaWrap>
            )}
        </Card>
    );
};

export default PlatformCardGoogle;
